import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { api, MOCK } from '../api/client';

export type PushStatus = 'enabled' | 'prompt' | 'denied' | 'unsupported' | 'unconfigured' | 'error';

export type PushResult = {
  status: PushStatus;
  /** Human-readable detail for the Settings card (plain English literals, no i18n keys). */
  detail?: string;
};

// ------------------------------------------------------------------ native (Capacitor: iOS = APNs token, Android = FCM token)

/** Hard bound for waiting on native registration events so Settings cannot hang forever. */
const NATIVE_REGISTRATION_TIMEOUT_MS = 12_000;

// Listener installation is awaited and deduplicated with a module-level promise
// (an early boolean flag races under StrictMode double-mounts).
let nativeListenersPromise: Promise<void> | null = null;
let nativeLastToken: string | null = null;

interface NativeInFlight {
  done: boolean;
  finish: (token: string | null) => void;
  promise: Promise<string | null>;
}
let nativeInFlight: NativeInFlight | null = null;

function onNativeRegistration(token: string): void {
  // Late events (even after a timeout) still record and re-sync the token.
  nativeLastToken = token;
  const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
  void api.registerPush(token, platform).catch(() => undefined);
  nativeInFlight?.finish(token);
}

function onNativeRegistrationError(): void {
  nativeInFlight?.finish(null);
}

function installNativeListeners(): Promise<void> {
  if (!nativeListenersPromise) {
    nativeListenersPromise = (async () => {
      // Capacitor addListener resolves asynchronously; install both before register().
      await PushNotifications.addListener('registration', (t) => onNativeRegistration(t.value));
      await PushNotifications.addListener('registrationError', () => onNativeRegistrationError());
    })().catch((err) => {
      // Allow a later user-initiated Enable attempt to retry listener installation
      // (the next call re-creates the promise) instead of being stuck until reload.
      nativeListenersPromise = null;
      throw err;
    });
  }
  return nativeListenersPromise;
}

/** One shared in-flight registration for concurrent callers; resolves with the token or null. */
function beginNativeRegistration(): Promise<string | null> {
  if (nativeInFlight && !nativeInFlight.done) return nativeInFlight.promise;
  let settle!: (token: string | null) => void;
  const promise = new Promise<string | null>((res) => { settle = res; });
  let timer: ReturnType<typeof setTimeout> | null = null;
  const flight: NativeInFlight = {
    done: false,
    finish: (token) => {
      if (flight.done) return;
      flight.done = true;
      nativeInFlight = null; // clear in-flight state safely (idempotent)
      if (timer) clearTimeout(timer);
      settle(token);
    },
    promise,
  };
  nativeInFlight = flight;
  timer = setTimeout(() => flight.finish(null), NATIVE_REGISTRATION_TIMEOUT_MS);
  void PushNotifications.register().catch(() => { flight.finish(null); });
  return promise;
}

async function registerNative(requestPermission: boolean): Promise<PushResult> {
  try {
    await installNativeListeners();
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' && requestPermission) {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive === 'denied') return { status: 'denied', detail: 'Notifications are blocked for this app. Enable them in system settings.' };
    // 'prompt' (or a dismissed prompt after an explicit request) is not 'denied' — the user may still allow it.
    if (perm.receive !== 'granted') return { status: 'prompt', detail: requestPermission ? 'You have not allowed notifications yet.' : 'Notifications are not enabled yet.' };
    if (nativeLastToken) {
      // Token already known (e.g. previous session); re-sync it to the server.
      void api.registerPush(nativeLastToken, Capacitor.getPlatform() === 'ios' ? 'ios' : 'android').catch(() => undefined);
      return { status: 'enabled' };
    }
    const token = await beginNativeRegistration();
    if (token) return { status: 'enabled' };
    return { status: 'error', detail: 'Could not register for native push notifications.' };
  } catch {
    return { status: 'error', detail: 'Native push registration failed.' };
  }
}

// ------------------------------------------------------------------ web (service worker + PushManager + VAPID)

function urlBase64ToUint8Array(b64: string): Uint8Array<ArrayBuffer> {
  const norm = b64.replace(/-/g, '+').replace(/_/g, '/');
  const pad = norm.length % 4 === 0 ? '' : '='.repeat(4 - (norm.length % 4));
  const raw = atob(norm + pad);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

function webSupported(): boolean {
  return typeof window !== 'undefined'
    && typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window
    && (window.isSecureContext === true || location.hostname === 'localhost' || location.hostname === '127.0.0.1');
}

async function fetchPublicKey(): Promise<string | null> {
  try {
    const r = await api.getPushPublicKey();
    return r.publicKey ?? null;
  } catch {
    return null;
  }
}

async function registerWeb(requestPermission: boolean): Promise<PushResult> {
  if (!webSupported()) return { status: 'unsupported', detail: 'Web push is not supported in this browser.' };
  if (MOCK) return { status: 'unsupported', detail: 'Push notifications are not available in mock mode.' };
  const perm = Notification.permission;
  if (perm === 'denied') return { status: 'denied', detail: 'Notifications are blocked for this site. Enable them in your browser settings.' };
  try {
    const sw = await navigator.serviceWorker.register('/push-sw.js');
    const existing = await sw.pushManager.getSubscription();
    if (perm !== 'granted' && !requestPermission) {
      // Silent mode: never prompt, but re-sync an existing subscription if there is one.
      if (existing) {
        void api.registerPush(JSON.stringify(existing), 'web').catch(() => undefined);
        return { status: 'enabled' };
      }
      return { status: 'prompt', detail: 'Notifications are not enabled yet.' };
    }
    let subscription = existing;
    if (!subscription) {
      if (perm !== 'granted') {
        const p = await Notification.requestPermission();
        if (p !== 'granted') {
          // A dismissed prompt is 'prompt' (may still be allowed later); only a hard block is 'denied'.
          return p === 'denied'
            ? { status: 'denied', detail: 'Notifications are blocked for this site.' }
            : { status: 'prompt', detail: 'You have not allowed notifications yet.' };
        }
      }
      const key = await fetchPublicKey();
      if (!key) return { status: 'unconfigured', detail: 'The server has no push (VAPID) key configured.' };
      subscription = await sw.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) });
    }
    await api.registerPush(JSON.stringify(subscription), 'web');
    return { status: 'enabled' };
  } catch {
    return { status: 'error', detail: 'Web push registration failed.' };
  }
}

// ------------------------------------------------------------------ public API

/**
 * Registers for push notifications (native via Capacitor, web via service worker + PushManager)
 * and syncs the token/subscription to the server.
 *
 * `requestPermission=false` (silent/sync mode) never triggers a fresh permission prompt;
 * it only re-registers already-granted native or existing web subscriptions.
 */
export async function registerPush(opts: { requestPermission?: boolean } = {}): Promise<PushResult> {
  const requestPermission = opts.requestPermission ?? false;
  try {
    if (Capacitor.isNativePlatform()) return await registerNative(requestPermission);
    return await registerWeb(requestPermission);
  } catch {
    return { status: 'error', detail: 'Push registration failed.' };
  }
}